import { useEffect, useRef } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router';

import { requestActionSelection, requestNodeSelection } from '@/redux/nodes/actions';
import { selectCondition } from '@/redux/nodes/actions';
import { RootState } from '@/shared/types/state';

const PARAMS = ['node', 'nodeAction', 'condition'];

export const useNodeDeepLink = () => {
  const dispatch = useDispatch();
  const { dialogId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const nodeId = searchParams.get('node');

  const targetLoaded = useSelector((state: RootState) =>
    Boolean(
      dialogId &&
      nodeId &&
      state.nodes.dialogId === dialogId &&
      state.nodes.nodes.some((node) => node.node_id === nodeId)
    )
  );

  const handledRef = useRef<string | null>(null);

  useEffect(() => {
    if (!nodeId) {
      handledRef.current = null;
      return;
    }
    if (!targetLoaded) {
      return;
    }

    const actionId = searchParams.get('nodeAction');
    const rawCondition = searchParams.get('condition');
    const conditionIndex = rawCondition === null ? null : Number(rawCondition);
    const key = `${nodeId}:${actionId ?? ''}:${rawCondition ?? ''}`;
    if (handledRef.current === key) {
      return;
    }
    handledRef.current = key;

    if (actionId) {
      dispatch(requestActionSelection({ node_id: nodeId, action_id: actionId }));
    } else if (conditionIndex !== null && Number.isInteger(conditionIndex)) {
      dispatch(selectCondition({ nodeId, index: conditionIndex }));
    } else {
      dispatch(requestNodeSelection(nodeId));
    }

    const next = new URLSearchParams(searchParams);
    PARAMS.forEach((param) => next.delete(param));
    setSearchParams(next, { replace: true });
  }, [dispatch, nodeId, targetLoaded, searchParams, setSearchParams]);
};
